import {
  Alert,
  Badge,
  Button,
  Grid,
  Group,
  NumberInput,
  Paper,
  Stack,
  Table,
  Text,
  Tooltip,
} from '@mantine/core';
import { DateInput } from '@mantine/dates';
import { modals } from '@mantine/modals';
import { notifications } from '@mantine/notifications';
import dayjs from 'dayjs';
import { Fragment, useMemo, useState } from 'react';
import {
  PiArrowsLeftRight,
  PiCheck,
  PiInfo,
  PiSlidersHorizontal,
  PiXCircle,
} from 'react-icons/pi';
import { useLocation } from 'wouter';
import { lineKey } from '@/models/lineKey';
import { TOffer, useUpdateOffer } from '@/models/offers';
import { useHasPermission } from '@/models/permissions';
import {
  LineRef,
  TSplitLine,
  TSplitPlanAltItem,
  TSplitPlanItem,
  useAdoptAltSplit,
  useCancelSplit,
  useProposeSplit,
  useSaveSplitPlan,
  useSetSplitEnabled,
  useSplitPlan,
  useWithdrawSplit,
} from '@/models/split-plan';
import { InstallmentContents } from '@/pages/offers/payments/InstallmentContents';
import { PartsList } from '../PartsList';
import PayerSection from '../PayerSection';
import InstallmentPlannerDrawer from '../payments/InstallmentPlannerDrawer';
import InvoiceDelivery from '../payments/InvoiceDelivery';
import RelatedInvoices from '../payments/RelatedInvoices';
import ScheduleStateBadge, {
  ScheduleState,
} from '../payments/ScheduleStateBadge';
import {
  DraftInstallment,
  deriveSplit,
  money,
  OrderedService,
} from '../payments/splitDerive';
import {
  itemsToDraft,
  useSplitReviewData,
} from '../payments/useSplitReviewData';

// Real, scoped BizDev permission (replaces the prototype `['offers:*','*:*']` wildcard).
const APPROVE_SPLIT_PERM = 'offers:approve-split';

/**
 * `restricted` is the Operations view: the payment schedule and the action to issue the
 * next installment, and nothing else. Payer, discount, invoice delivery and the invoice
 * list are all either outside their remit or backed by endpoints their role cannot call.
 */
export default function FinanceTab({
  offer,
  restricted = false,
}: {
  offer: TOffer;
  restricted?: boolean;
}) {
  const { data: planData } = useSplitPlan(offer.id);

  const splitEnabled = planData?.splitEnabled ?? false;
  const selectedLines = planData?.selectedLines ?? [];

  if (restricted) {
    return selectedLines.length === 0 ? (
      <Alert color="blue" icon={<PiInfo size={16} />}>
        <Text fz="sm">
          This offer has no confirmed split-payment schedule yet, so there are
          no installments to issue.
        </Text>
      </Alert>
    ) : (
      <AcceptedSplit offer={offer} restricted />
    );
  }

  return (
    <Grid gap="md">
      <Grid.Col span={{ base: 12, md: 8 }}>
        <Stack gap="md">
          {!splitEnabled ? (
            <RelatedInvoices
              offer={offer}
              canSplit={false}
              onSplit={() => {}}
            />
          ) : selectedLines.length === 0 ? (
            <>
              <RelatedInvoices
                offer={offer}
                canSplit={false}
                onSplit={() => {}}
              />
              <Alert color="blue" icon={<PiInfo size={16} />}>
                <Text fz="sm">
                  Split Payment enabled — awaiting the customer to confirm their
                  selection.
                </Text>
              </Alert>
            </>
          ) : (
            <AcceptedSplit offer={offer} />
          )}
        </Stack>
      </Grid.Col>
      <Grid.Col span={{ base: 12, md: 4 }}>
        <Stack gap="md">
          <DiscountCard offer={offer} />
          <PayerSection offer={offer} />
          <InvoiceDelivery offer={offer} />
          <PaymentTermsCard offer={offer} />
        </Stack>
      </Grid.Col>
    </Grid>
  );
}

function DiscountCard({ offer }: { offer: TOffer }) {
  const canWrite = useHasPermission('offers:write');
  const { data: planData } = useSplitPlan(offer.id);
  const update = useUpdateOffer();
  const [value, setValue] = useState<string | number>(
    Number(offer.discountPercentage) || 0,
  );
  const savedDeadline = offer.discountExpiresAt
    ? dayjs(offer.discountExpiresAt).format('YYYY-MM-DD')
    : null;
  const [deadline, setDeadline] = useState<string | null>(savedDeadline);
  const selectionConfirmed = (planData?.selectedLines ?? []).length > 0;
  const editable = canWrite && !offer.accepted && !selectionConfirmed;
  const dirty =
    Number(value) !== Number(offer.discountPercentage) ||
    deadline !== savedDeadline;
  const lapsed =
    !!offer.discountExpiresAt &&
    dayjs(offer.discountExpiresAt).isBefore(dayjs()) &&
    !selectionConfirmed &&
    !offer.accepted;

  const deadlineIso = () => {
    if (!deadline) return null;
    const end = dayjs(deadline).endOf('day');
    return offer.expiresAt && end.isAfter(dayjs(offer.expiresAt))
      ? dayjs(offer.expiresAt).toISOString()
      : end.toISOString();
  };

  return (
    <Paper withBorder p="md">
      <Stack gap="sm">
        <Text fw={600} fz="sm">
          Discount
        </Text>
        {selectionConfirmed && !offer.accepted && (
          <Text fz="xs" c="dimmed">
            Locked once the customer confirms their selection.
          </Text>
        )}
        <Group align="flex-end" gap="xs" wrap="nowrap">
          <NumberInput
            label="Discount"
            value={value}
            onChange={setValue}
            min={0}
            max={10}
            step={0.5}
            suffix="%"
            size="xs"
            w={90}
            disabled={!editable}
          />
          <DateInput
            label="Valid until"
            placeholder={
              offer.expiresAt
                ? `Same as offer (${dayjs(offer.expiresAt).format('DD.MM.YYYY')})`
                : 'No deadline'
            }
            value={deadline}
            onChange={setDeadline}
            minDate={new Date()}
            maxDate={offer.expiresAt ? new Date(offer.expiresAt) : undefined}
            valueFormat="DD.MM.YYYY"
            clearable
            size="xs"
            style={{ flex: 1 }}
            disabled={!editable || !(Number(value) > 0)}
          />
        </Group>
        <Text fz="xs" c={lapsed ? 'orange' : 'dimmed'}>
          {lapsed
            ? 'The deadline has passed: the discount no longer applies to this customer.'
            : 'The customer sees a countdown; the offer itself stays open until it expires.'}
        </Text>
        {editable && (
          <Group justify="flex-end">
            <Button
              size="xs"
              disabled={!dirty}
              loading={update.isPending}
              onClick={() =>
                update.mutate(
                  {
                    offerId: offer.id,
                    discountPercentage: Number(value),
                    discountExpiresAt: Number(value) > 0 ? deadlineIso() : null,
                  },
                  {
                    onSuccess: () =>
                      notifications.show({
                        color: 'teal',
                        message: 'Discount applied.',
                      }),
                    onError: (err) =>
                      notifications.show({
                        color: 'red',
                        message: String(err),
                      }),
                  },
                )
              }
            >
              Apply
            </Button>
          </Group>
        )}
      </Stack>
    </Paper>
  );
}
function PaymentTermsCard({ offer }: { offer: TOffer }) {
  const { data: planData } = useSplitPlan(offer.id);
  const canWrite = useHasPermission('offers:write');
  const setSplitEnabled = useSetSplitEnabled(offer.id);

  const splitEnabled = planData?.splitEnabled ?? false;
  const hasPlan = planData?.plan != null;
  const selectionConfirmed =
    (planData?.selectedLines ?? []).length > 0 || hasPlan;
  // An accepted offer is frozen and its invoice already issued - payment terms
  // must never change afterwards.
  const isLocked = offer.accepted || selectionConfirmed;
  // A customer who changes their mind can go back to a single invoice, but only
  // once the installment plan is cancelled, whatever state it had reached.
  const canCancelSplitMode =
    canWrite &&
    splitEnabled &&
    !offer.accepted &&
    selectionConfirmed &&
    !hasPlan;

  const toggle = (enabled: boolean) =>
    setSplitEnabled.mutate(enabled, {
      onError: (err) =>
        notifications.show({ color: 'red', message: String(err) }),
    });

  const confirmCancelSplitMode = () =>
    modals.openConfirmModal({
      title: 'Cancel Split Mode?',
      children: (
        <Text fz="sm">
          The offer goes back to a single invoice and the customer's selection
          is cleared. The customer will choose their services again on the offer
          page and pay with one invoice.
        </Text>
      ),
      labels: { confirm: 'Cancel Split Mode', cancel: 'Keep split mode' },
      confirmProps: { color: 'red' },
      onConfirm: () =>
        setSplitEnabled.mutate(false, {
          onSuccess: () =>
            notifications.show({
              color: 'green',
              message: 'Switched back to a single invoice.',
            }),
          onError: (err) =>
            notifications.show({ color: 'red', message: String(err) }),
        }),
    });

  return (
    <Paper withBorder p="md">
      <Stack gap="sm">
        <Text fw={600} fz="sm">
          Payment Terms
        </Text>
        <Text fz="sm" c="dimmed">
          {splitEnabled
            ? 'Split Payment Mode — the Offer is expected to be paid in installments.'
            : 'Currently Offer is expected to be paid with single invoice.'}
        </Text>
        {splitEnabled && selectionConfirmed && !offer.accepted && hasPlan && (
          <Text fz="xs" c="dimmed">
            To switch back to a single invoice, cancel the installment plan
            first.
          </Text>
        )}
        {canCancelSplitMode && (
          <Group justify="space-between" wrap="nowrap">
            <Text fz="xs" c="dimmed">
              The customer has chosen services for split payment. Cancelling
              split mode clears their selection.
            </Text>
            <Button
              size="xs"
              color="red"
              variant="light"
              loading={setSplitEnabled.isPending}
              onClick={confirmCancelSplitMode}
            >
              Cancel Split Mode
            </Button>
          </Group>
        )}
        {canWrite && !isLocked && (
          <Group justify="flex-end">
            <Button
              size="xs"
              variant={splitEnabled ? 'light' : 'filled'}
              loading={setSplitEnabled.isPending}
              onClick={() => toggle(!splitEnabled)}
            >
              {splitEnabled
                ? 'Switch to Single Invoice'
                : 'Enable Split Payment Mode'}
            </Button>
          </Group>
        )}
      </Stack>
    </Paper>
  );
}

function AcceptedSplit({
  offer,
  restricted = false,
}: {
  offer: TOffer;
  restricted?: boolean;
}) {
  const [, nav] = useLocation();
  const canApprove = useHasPermission(APPROVE_SPLIT_PERM);
  const canPropose = useHasPermission('offers:write');
  const canIssue = useHasPermission('offers:issue-installment-all');
  const {
    planData,
    isLoading,
    base,
    services,
    currency,
    accountId,
    orderNetMinor,
    orderNetCurrency,
  } = useSplitReviewData(offer.id, canApprove);

  const save = useSaveSplitPlan(offer.id);
  const propose = useProposeSplit(offer.id);
  const cancel = useCancelSplit(offer.id);
  const withdraw = useWithdrawSplit(offer.id);
  const [plannerOpen, setPlannerOpen] = useState(false);

  const status: ScheduleState = planData?.plan?.status ?? 'none';
  const srEditable =
    canPropose &&
    (status === 'none' || status === 'draft' || status === 'returned');

  const seedKey = `${JSON.stringify(planData?.items ?? [])}|${currency}`;
  const [draftState, setDraftState] = useState<{
    key: string;
    items: DraftInstallment[];
  }>({
    key: '',
    items: [],
  });
  const draft =
    draftState.key === seedKey
      ? draftState.items
      : itemsToDraft(planData?.items ?? [], currency);
  if (draftState.key !== seedKey) {
    setDraftState({ key: seedKey, items: draft });
  }
  const setDraft = (items: DraftInstallment[]) =>
    setDraftState({ key: seedKey, items });

  // The order's value as the SERVER measures it. Deriving it here instead added prices
  // across currencies on a mixed order, which is how this planner came to show a total the
  // server then refused - a green tick followed by a failed submit.
  const d = deriveSplit(draft, services, orderNetMinor);

  const persist = () =>
    save.mutateAsync(
      draft
        .sort((a, b) => a.seq - b.seq)
        .map((i) => ({
          seq: i.seq,
          currency: i.currency,
          paymentMethod: i.paymentMethod,
          payerId: i.payerId ?? null,
          amount: i.amount ?? null,
          lines: [],
        })),
    );

  const handlePropose = async (note: string) => {
    await persist();
    await propose.mutateAsync(note);
  };

  if (base.length === 0) {
    return (
      <Paper withBorder p="md">
        <Group gap="xs" mb="xs">
          <Text fw={500}>Split payment schedule</Text>
          <ScheduleStateBadge state={status} />
        </Group>
        <Alert color="yellow" icon={<PiInfo size={16} />}>
          <Text fz="sm">
            No ordered services found on this offer's selection to distribute.
          </Text>
        </Alert>
      </Paper>
    );
  }

  const committed = status === 'committed';
  const parts = planData?.parts ?? [];

  return (
    <>
      <Paper withBorder p="md">
        <Stack gap="md">
          <Text fw={500}>Split payment schedule</Text>

          {committed && (
            <Stack gap="md">
              <CollectedRollup
                parts={parts}
                planItems={planData?.items ?? []}
                offerStatus={planData?.offerStatus}
              />
              <PartsList
                parts={parts}
                offerId={offer.id}
                accountId={accountId}
                currencyTotals={planData?.offerStatus?.currencyTotals}
                planItems={planData?.items ?? []}
                selectedLines={planData?.selectedLines ?? []}
                issuableSeq={planData?.issuableSeq ?? null}
                canIssue={canIssue}
                canReadPayers={!restricted}
                recipientBlockReason={
                  offer.prospectiveInvoiceRecipient?.blocked
                    ? (offer.prospectiveInvoiceRecipient.blockReason ??
                      'The paying party has no verified email address')
                    : null
                }
              />
            </Stack>
          )}

          {status === 'returned' && planData?.plan?.reviewNote && (
            <Alert
              color="orange"
              title="BizDev returned this schedule"
              icon={<PiInfo size={16} />}
            >
              <Text fz="sm">{planData.plan.reviewNote}</Text>
            </Alert>
          )}

          {!committed && (
            <SplitPlanLauncher
              status={status}
              srEditable={srEditable}
              canWithdraw={canPropose}
              canCancel={canPropose}
              withdrawing={withdraw.isPending}
              cancelling={cancel.isPending}
              onCancel={() => cancel.mutate()}
              draft={draft}
              derived={d}
              selectedLines={planData?.selectedLines ?? []}
              onOpen={() => setPlannerOpen(true)}
              onWithdraw={() =>
                withdraw.mutate(undefined, {
                  onSuccess: () =>
                    notifications.show({
                      color: 'teal',
                      message: 'Plan pulled back to draft.',
                    }),
                  onError: (err) =>
                    notifications.show({ color: 'red', message: String(err) }),
                })
              }
            />
          )}

          {canApprove && status === 'proposed' && (
            <Alert color="blue" icon={<PiInfo size={16} />}>
              <Group justify="space-between" wrap="wrap" gap="xs">
                <Text fz="sm">This schedule is awaiting BizDev review.</Text>
                <Button
                  size="xs"
                  variant="light"
                  onClick={() => nav(`/split-plans/${offer.id}`)}
                >
                  Open in Split Plans
                </Button>
              </Group>
            </Alert>
          )}

          {status === 'returned' &&
            (planData?.plan?.reviewAltItems?.length ?? 0) > 0 && (
              <AltProposalDiff
                offerId={offer.id}
                currency={currency}
                current={draft}
                alt={planData!.plan!.reviewAltItems!}
                altFingerprint={planData?.altFingerprint}
                services={services}
                canAdopt={canPropose}
              />
            )}
        </Stack>
      </Paper>

      {!committed && (
        <InstallmentPlannerDrawer
          opened={plannerOpen}
          onClose={() => setPlannerOpen(false)}
          status={status}
          editable={srEditable}
          installments={draft}
          setInstallments={setDraft}
          services={services}
          // Amounts are typed, totalled and reconciled in the plan's base currency, so the
          // planner is labelled with that - not with whichever currency the order's first
          // line happens to be priced in.
          currency={orderNetCurrency}
          accountId={accountId}
          derived={d}
          offer={offer}
          saving={save.isPending}
          proposing={propose.isPending}
          showCogs={false}
          onSave={() => persist()}
          onPropose={async (note) => {
            await handlePropose(note);
            setPlannerOpen(false);
          }}
        />
      )}

      {isLoading && (
        <Text size="xs" c="dimmed">
          Loading schedule…
        </Text>
      )}
    </>
  );
}

function SplitPlanLauncher({
  status,
  srEditable,
  canWithdraw,
  canCancel,
  withdrawing,
  cancelling,
  onCancel,
  draft,
  derived,
  selectedLines,
  onOpen,
  onWithdraw,
}: {
  status: ScheduleState;
  srEditable: boolean;
  canWithdraw: boolean;
  canCancel: boolean;
  withdrawing: boolean;
  cancelling: boolean;
  onCancel: () => void;
  draft: DraftInstallment[];
  derived: ReturnType<typeof deriveSplit>;
  selectedLines: TSplitLine[];
  onOpen: () => void;
  onWithdraw: () => void;
}) {
  const sorted = [...draft].sort((a, b) => a.seq - b.seq);
  const currency = sorted[0]?.currency ?? 'EUR';
  const cancellable =
    canCancel && ['draft', 'proposed', 'returned', 'approved'].includes(status);

  return (
    <Stack gap="sm">
      {sorted.length > 0 && (
        <Table verticalSpacing={6} fz="sm">
          <Table.Tbody>
            {sorted.map((inst) => {
              const amount = Number(inst.amount ?? 0);
              const pct =
                derived.orderedTotal > 0
                  ? Math.round((amount / derived.orderedTotal) * 100)
                  : 0;
              return (
                <Fragment key={inst.key}>
                  <Table.Tr>
                    <Table.Td w={120}>
                      <Text fz="sm" fw={500}>
                        #{inst.seq}
                      </Text>
                    </Table.Td>
                    <Table.Td ta="right">
                      <Text fz="sm">{money(amount, inst.currency)}</Text>
                    </Table.Td>
                    <Table.Td w={80} ta="right">
                      <Badge size="sm" variant="light" color="gray">
                        {pct}%
                      </Badge>
                    </Table.Td>
                  </Table.Tr>
                  <Table.Tr>
                    <Table.Td />
                    <Table.Td colSpan={2} pt={0}>
                      <InstallmentContents
                        coveredLines={inst.lines}
                        note={inst.note ?? null}
                        selectedLines={selectedLines}
                        emptyLabel="Services not assigned yet"
                      />
                    </Table.Td>
                  </Table.Tr>
                </Fragment>
              );
            })}
            <Table.Tr>
              <Table.Td w={120}>
                <Text fz="xs" c="dimmed">
                  Billed / total
                </Text>
              </Table.Td>
              <Table.Td ta="right">
                <Text
                  fz="sm"
                  fw={600}
                  c={derived.amountsSumToTotal ? 'teal' : 'orange'}
                >
                  {money(derived.amountsTotal, currency)} /{' '}
                  {money(derived.orderedTotal, currency)}
                </Text>
              </Table.Td>
              <Table.Td w={80} />
            </Table.Tr>
          </Table.Tbody>
        </Table>
      )}

      <Group gap="xs">
        {cancellable && (
          <Tooltip label="Abandon this draft schedule">
            <Button
              size="xs"
              variant="subtle"
              color="red"
              leftSection={<PiXCircle size={12} />}
              loading={cancelling}
              onClick={onCancel}
            >
              Cancel schedule
            </Button>
          </Tooltip>
        )}
        {status === 'proposed' && canWithdraw && (
          <Tooltip label="Pull the plan back to draft to fix it before BizDev reviews">
            <Button
              size="xs"
              variant="light"
              color="orange"
              loading={withdrawing}
              onClick={onWithdraw}
            >
              Withdraw from review
            </Button>
          </Tooltip>
        )}
        {srEditable && (
          <Button
            size="xs"
            leftSection={<PiSlidersHorizontal size={14} />}
            onClick={onOpen}
            data-testid="ux-open-planner"
          >
            Plan installments
          </Button>
        )}
      </Group>
    </Stack>
  );
}

function AltProposalDiff({
  offerId,
  currency,
  current,
  alt,
  altFingerprint,
  services,
  canAdopt,
}: {
  offerId: number;
  currency: string;
  current: DraftInstallment[];
  alt: TSplitPlanAltItem[];
  /** Server-computed identity of the alternative being rendered. */
  altFingerprint?: string | null;
  services: OrderedService[];
  canAdopt: boolean;
}) {
  const adopt = useAdoptAltSplit(offerId);
  const saleOf = useMemo(() => {
    const m = new Map(services.map((s) => [lineKey(s.lineRef), s.saleValue]));
    return (refs: LineRef[]) =>
      refs.reduce((sum, ref) => sum + (m.get(lineKey(ref)) ?? 0), 0);
  }, [services]);

  const cur = [...current].sort((a, b) => a.seq - b.seq);
  const altSorted = [...alt].sort((a, b) => a.seq - b.seq);
  const rows = Math.max(cur.length, altSorted.length);

  return (
    <Paper withBorder p="sm" bg="var(--mantine-color-orange-light)">
      <Stack gap="sm">
        <Group gap="xs">
          <PiArrowsLeftRight size={16} />
          <Text fw={500} fz="sm">
            BizDev proposed an alternative schedule
          </Text>
        </Group>
        <Table withRowBorders verticalSpacing="xs" fz="xs">
          <Table.Thead>
            <Table.Tr>
              <Table.Th w={40}>#</Table.Th>
              <Table.Th>Current sum</Table.Th>
              <Table.Th>Alternative sum</Table.Th>
            </Table.Tr>
          </Table.Thead>
          <Table.Tbody>
            {Array.from({ length: rows }).map((_, i) => {
              const c = cur[i];
              const a = altSorted[i];
              const cSum = c ? (c.amount ?? saleOf(c.lines)) : null;
              const aSum = a
                ? a.amount != null
                  ? Number(a.amount)
                  : saleOf(a.lines)
                : null;
              const sumChanged = cSum !== aSum;
              return (
                <Table.Tr key={i}>
                  <Table.Td>{i + 1}</Table.Td>
                  <Table.Td>
                    {cSum != null ? money(cSum, currency) : '—'}
                  </Table.Td>
                  <Table.Td
                    c={sumChanged ? 'orange' : undefined}
                    fw={sumChanged ? 600 : undefined}
                  >
                    {aSum != null ? (
                      <Group gap={4} wrap="nowrap">
                        {money(aSum, currency)}
                        {sumChanged && (
                          <Badge size="xs" variant="light" color="orange">
                            changed
                          </Badge>
                        )}
                      </Group>
                    ) : (
                      '—'
                    )}
                  </Table.Td>
                </Table.Tr>
              );
            })}
          </Table.Tbody>
        </Table>
        {canAdopt && (
          <Group>
            <Button
              size="xs"
              color="orange"
              leftSection={<PiCheck size={14} />}
              loading={adopt.isPending}
              onClick={() => adopt.mutate(altFingerprint)}
            >
              Adopt alternative
            </Button>
            <Text fz="xs" c="dimmed">
              Approves BizDev's schedule and shows it to the customer
              immediately. To change any amount, edit the schedule first — that
              makes it a new draft and needs another review.
            </Text>
          </Group>
        )}
      </Stack>
    </Paper>
  );
}

function CollectedRollup({
  parts,
  planItems,
  offerStatus,
}: {
  parts: { status: string }[];
  planItems: TSplitPlanItem[];
  offerStatus?: {
    currencyTotals: Record<
      string,
      { paid: number; outstanding: number }
    > | null;
  } | null;
}) {
  const total = planItems.length || parts.length;
  const paid = parts.filter((p) => p.status === 'paid').length;
  const totals = offerStatus?.currencyTotals || {};
  const entries = Object.entries(totals);

  return (
    <Paper withBorder p="sm" bg="var(--mantine-color-gray-light)">
      <Group justify="space-between" wrap="wrap" gap="xs">
        <Text fz="sm" fw={500}>
          {paid} of {total} installments paid
        </Text>
        <Group gap="md">
          {entries.length > 0 ? (
            entries.map(([cur, t]) => (
              <Text key={cur} fz="sm">
                {Math.round(t.paid).toLocaleString()} /{' '}
                {Math.round(t.paid + t.outstanding).toLocaleString()} {cur}{' '}
                collected
              </Text>
            ))
          ) : (
            <Text fz="sm" c="dimmed">
              No payments yet
            </Text>
          )}
        </Group>
      </Group>
    </Paper>
  );
}
